import React, { useState } from 'react';
import { Alert, Button, Flex, Modal, Typography } from '@duxton-web/ui';

import { JSON_SCHEMA_VERSION_DRAFT_07 } from '@/constants/jsonSchema';
import JsonEditor from '@/pages/ExperimentFormV2/common/JsonEditor';
import { convertJsonToSchema } from '@/utils/jsonSchemaConverter';

interface JsonSchemaConverterModalProps {
  /**
   * Callback when schema is successfully generated
   */
  onSchemaGenerated: (schema: string) => void;

  /**
   * Whether the button is disabled
   */
  disabled?: boolean;

  /**
   * Title for the generated schema
   */
  schemaTitle?: string;
}

/**
 * Self-contained modal component for converting JSON data to JSON Schema.
 * Includes trigger button and manages its own state.
 */
const JsonSchemaConverterModal: React.FC<JsonSchemaConverterModalProps> = ({
  onSchemaGenerated,
  disabled = false,
  schemaTitle = 'Generated schema',
}) => {
  const [open, setOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [converting, setConverting] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);

  const handleJsonInputChange = (value: string) => {
    setJsonInput(value);
    // Clear error when user modifies the JSON
    if (conversionError) {
      setConversionError(null);
    }
  };

  const handleConvert = async () => {
    setConverting(true);
    setConversionError(null);

    try {
      const schema = await convertJsonToSchema(jsonInput, {
        schemaVersion: JSON_SCHEMA_VERSION_DRAFT_07,
        title: schemaTitle,
      });

      // Callback with generated schema
      onSchemaGenerated(schema);

      // Close modal and reset
      setOpen(false);
      setJsonInput('');
    } catch (error) {
      setConversionError(error.message);
    } finally {
      setConverting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setJsonInput('');
    setConversionError(null);
  };

  return (
    <>
      <Button
        type="primary"
        size="small"
        onClick={() => setOpen(true)}
        disabled={disabled}
        style={{ width: 'fit-content' }}
      >
        Generate Schema
      </Button>

      <Modal
        title={
          <Typography.Title
            level={3}
            style={{
              height: 44,
            }}
          >
            Convert JSON to Schema
          </Typography.Title>
        }
        open={open}
        onCancel={handleClose}
        onOk={handleConvert}
        okText="Convert to Schema"
        cancelText="Cancel"
        confirmLoading={converting}
        width={700}
        okButtonProps={{ size: 'small' }}
        cancelButtonProps={{ size: 'small' }}
        centered
        styles={{
          body: {
            height: 'calc(100vh - 208px)',
            overflowY: 'auto',
          },
        }}
        maskClosable={false}
      >
        <Flex vertical gap={16} style={{ height: '100%' }}>
          <Alert
            type="info"
            message="Paste your JSON data below to convert it to a JSON Schema (draft-07)"
            showIcon
          />

          <Flex vertical gap={4} style={{ flex: 1 }}>
            <Typography.Text strong>JSON Data:</Typography.Text>
            <JsonEditor
              data={jsonInput}
              onJsonChange={handleJsonInputChange}
              readOnly={false}
              height="600px"
            />
            {conversionError && (
              <Typography.Text type="danger">{conversionError}</Typography.Text>
            )}
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};

export default JsonSchemaConverterModal;
